/**
 * Safely converts an ISO date string to a numeric timestamp (ms since epoch).
 */
export function toTimestamp(date: string | null | undefined): number {
  if (!date) return 0;

  const parsed = new Date(date).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Sorts an array of entities by their updatedAt timestamp descending (most recent first).
 * Supports both summaries (which have .updatedAt) and full entities (which have .audit?.updatedAt).
 */
export function sortEntitiesByUpdatedAt<T extends { updatedAt?: string | null, audit?: { updatedAt?: string | null } }>(entities: T[]): T[] {
  return [...entities].sort((a, b) => {
    const timeA = toTimestamp(a.updatedAt || a.audit?.updatedAt);
    const timeB = toTimestamp(b.updatedAt || b.audit?.updatedAt);
    return timeB - timeA;
  });
}

/**
 * Safely converts an ISO date string to a Date object.
 */
export function toDate(date: string | null | undefined): Date {
  if (!date) return new Date(0);

  const d = new Date(date);
  return isNaN(d.getTime()) ? new Date(0) : d;
}

/**
 * Recursively scans an object or array and converts any Firestore Timestamps to ISO strings.
 * Used at the DAO/boundary level to ensure schema compatibility.
 */
export function normalizeDates<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;

  // Handle Firestore Timestamps
  if (typeof obj === 'object' && 'toDate' in obj && typeof (obj as any).toDate === 'function') {
    return (obj as any).toDate().toISOString() as unknown as T;
  }

  // Handle Arrays
  if (Array.isArray(obj)) {
    return obj.map(item => normalizeDates(item)) as unknown as T;
  }

  // Handle Objects
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[key] = normalizeDates(value);
    }
    return result as T;
  }

  return obj;
}
